import { useMemo } from "react";
import { Alert, Box, Button as MuiButton, Stack, Typography as MuiTypography } from "@mui/material";
import { Button as FoodsButton, Typography as FoodsTypography } from "@foodpilot/foods";
import { useTranslation } from "react-i18next";

import { useIngredientSupplierTransports } from "src/api/client-api/ingredientSupplierTransports/useIngredientSupplierTransports";
import { INGREDIENT_LEVEL_STAGES } from "src/api/client-api/transport/transportStages";
import { useEnabledTransportStages } from "src/api/client-api/transport/useEnabledTransportStages";
import { useTransportCircuits } from "src/api/client-api/transport/useTransportCircuits";
import { useSearchTransportModes } from "src/api/sure-api/useSearchTransportModes";
import { RemoteErrorAlert } from "src/components/PageState/RemoteErrorAlert";
import { useFoodpilotContext } from "src/context/FoodpilotContext";
import { getTransportCircuitModeIcons } from "src/utils/transport/modeIcons";
import { getTransportCircuitMeta } from "src/utils/transport/resourceMeta";

import type {
    IngredientSupplierTransportAppearance,
    IngredientSupplierTransportSelection,
} from "./ingredientSupplierTransportTypes";
import { TransportResourceCard } from "./TransportResourceCard";

const extractIdFromIri = (iri?: string): number | undefined => {
    if (iri === undefined) {
        return undefined;
    }

    const rawId = iri.split("/").pop();
    const id = rawId === undefined ? NaN : Number.parseInt(rawId, 10);

    return Number.isNaN(id) ? undefined : id;
};

export const IngredientSupplierTransportSummary = ({
    appearance = "legacy",
    value,
    dense = false,
    variant = "card",
}: {
    appearance?: IngredientSupplierTransportAppearance;
    value?: IngredientSupplierTransportSelection;
    dense?: boolean;
    variant?: "card" | "inline";
}) => {
    const { t } = useTranslation();
    const { isStageEnabled } = useEnabledTransportStages();
    const { transportModes } = useSearchTransportModes();
    const selectedTransports = INGREDIENT_LEVEL_STAGES.flatMap((stage) => {
        if (!isStageEnabled(stage)) {
            return [];
        }

        const circuit = value?.[stage]?.transportCircuit;
        return circuit ? [{ stage, circuit }] : [];
    });

    if (selectedTransports.length === 0) {
        return null;
    }

    if (variant === "inline") {
        return (
            <Stack data-testid="ingredient-supplier-transport-inline-summary" gap={0.75} sx={{ minWidth: 0 }}>
                {selectedTransports.map(({ stage, circuit }) => {
                    const route = `${circuit.originCountryCode ?? "-"} → ${circuit.destinationCountryCode ?? "-"}`;
                    const meta = getTransportCircuitMeta(circuit, t);
                    const stageFallback = stage
                        .replace(/_/g, " ")
                        .replace(/^./, (firstLetter) => firstLetter.toUpperCase());

                    return (
                        <Stack
                            key={`${stage}-${circuit.id}`}
                            gap={0.25}
                            sx={(theme) => ({
                                backgroundColor: theme.custom.grey[200],
                                borderRadius: theme.spacing(1),
                                minWidth: 0,
                                padding: theme.spacing(1),
                            })}
                        >
                            {appearance === "product-v2" ?
                                <>
                                    <FoodsTypography variant="body-semibold" noWrap>
                                        {circuit.name ?? `#${circuit.id}`}
                                    </FoodsTypography>
                                    <FoodsTypography variant="small-semibold" noWrap>
                                        {t(`transport.stage.${stage}`, stageFallback)}
                                    </FoodsTypography>
                                    <FoodsTypography variant="small-regular" noWrap>
                                        {[route, ...meta].join(" · ")}
                                    </FoodsTypography>
                                </>
                            :   <>
                                    <MuiTypography variant="body2" fontWeight="fontWeightBold" noWrap>
                                        {circuit.name ?? `#${circuit.id}`}
                                    </MuiTypography>
                                    <MuiTypography variant="caption" fontWeight="fontWeightBold" noWrap>
                                        {t(`transport.stage.${stage}`, stageFallback)}
                                    </MuiTypography>
                                    <MuiTypography variant="caption" noWrap>
                                        {[route, ...meta].join(" · ")}
                                    </MuiTypography>
                                </>
                            }
                        </Stack>
                    );
                })}
            </Stack>
        );
    }

    return (
        <Stack gap={dense ? 0.75 : 1}>
            {selectedTransports.map(({ stage, circuit }) => (
                <Box
                    key={`${stage}-${circuit.id}`}
                    sx={(theme) => ({
                        maxWidth: dense ? theme.spacing(40) : undefined,
                        "& .MuiCard-root": {
                            boxShadow: dense ? "none" : undefined,
                        },
                        "& .MuiCard-root > .MuiBox-root": {
                            p: dense ? 1 : 2,
                        },
                        "& .MuiCardActionArea-root": {
                            p: dense ? 1 : 2,
                        },
                    })}
                >
                    <TransportResourceCard
                        appearance={appearance}
                        kind="circuit"
                        title={circuit.name ?? `#${circuit.id}`}
                        subtitle={`${circuit.originCountryCode ?? "-"} -> ${circuit.destinationCountryCode ?? "-"}`}
                        modeIcons={getTransportCircuitModeIcons(circuit, transportModes)}
                        meta={[
                            t(`transport.stage.${stage}`, stage.replace(/_/g, " ")),
                            ...getTransportCircuitMeta(circuit, t),
                        ]}
                        selectable={false}
                    />
                </Box>
            ))}
        </Stack>
    );
};

export type IngredientSupplierTransportControlProps = {
    appearance?: IngredientSupplierTransportAppearance;
    disabled?: boolean;
    ingredientSupplierId?: number;
    summaryVariant?: "card" | "inline";
    value?: IngredientSupplierTransportSelection;
    onConfigure: () => void;
};

export const IngredientSupplierTransportControl = ({
    appearance = "legacy",
    disabled = false,
    ingredientSupplierId,
    summaryVariant = "card",
    value,
    onConfigure,
}: IngredientSupplierTransportControlProps) => {
    const { t } = useTranslation();
    const { selectedCompany } = useFoodpilotContext();
    const {
        ingredientSupplierTransports: loadedIngredientSupplierTransports,
        isError: areIngredientSupplierTransportsErrored,
    } = useIngredientSupplierTransports({
        organizationId: selectedCompany?.id,
        ingredientSupplierId,
        enabled: selectedCompany?.id !== undefined && ingredientSupplierId !== undefined,
    });
    const {
        data: transportCircuitsData,
        transportCircuits: loadedTransportCircuits,
        isError: areTransportCircuitsErrored,
    } = useTransportCircuits({
        organizationId: selectedCompany?.id,
        enabled: ingredientSupplierId !== undefined && selectedCompany?.id !== undefined,
    });
    const ingredientSupplierTransports = useMemo(
        () => loadedIngredientSupplierTransports ?? [],
        [loadedIngredientSupplierTransports],
    );
    const transportCircuits = loadedTransportCircuits ?? [];
    const hasUnresolvedCircuit =
        transportCircuitsData !== undefined &&
        ingredientSupplierTransports.some((transport) => {
            const circuitIri = transport.transportCircuit;
            return (
                typeof circuitIri === "string" &&
                transportCircuits.every((circuit) => circuit.id !== extractIdFromIri(circuitIri))
            );
        });
    const persistedSelections = useMemo<IngredientSupplierTransportSelection>(() => {
        const next: IngredientSupplierTransportSelection = {};

        for (const transport of ingredientSupplierTransports) {
            if (typeof transport.transportCircuit === "string") {
                continue;
            }

            next[transport.stage] = {
                id: transport.id,
                transportCircuit: transport.transportCircuit,
            };
        }

        return next;
    }, [ingredientSupplierTransports]);
    const transportSelections = { ...persistedSelections, ...value };
    const hasTransport = Object.values(transportSelections).some((selection) => selection?.transportCircuit != null);
    const hasCollectionError = areIngredientSupplierTransportsErrored || areTransportCircuitsErrored;

    return (
        <Box
            sx={{
                alignItems: appearance === "product-v2" ? "stretch" : "flex-start",
                display: "flex",
                flexDirection: "column",
                gap: 1,
                minWidth: 0,
            }}
        >
            {hasCollectionError &&
                (appearance === "product-v2" ?
                    <RemoteErrorAlert message={t("common.error_occurred", "An error occurred")} status="server-error" />
                :   <Alert severity="error">{t("common.error_occurred", "An error occurred")}</Alert>)}
            {hasUnresolvedCircuit &&
                (appearance === "product-v2" ?
                    <RemoteErrorAlert
                        message={t(
                            "ingredientSupplier.transport.unresolvedCircuit",
                            "A saved transport circuit is no longer available.",
                        )}
                        status="contract-error"
                    />
                :   <Alert severity="error">
                        {t(
                            "ingredientSupplier.transport.unresolvedCircuit",
                            "A saved transport circuit is no longer available.",
                        )}
                    </Alert>)}
            {hasTransport && (
                <IngredientSupplierTransportSummary
                    appearance={appearance}
                    value={transportSelections}
                    dense={summaryVariant === "card"}
                    variant={summaryVariant}
                />
            )}
            {appearance === "product-v2" ?
                <FoodsButton
                    size="small"
                    variant="text"
                    disabled={disabled || hasCollectionError}
                    onClick={onConfigure}
                    sx={{ alignSelf: "flex-start" }}
                >
                    {hasTransport ?
                        t("ingredientSupplier.transport.edit", "Edit")
                    :   t("ingredientSupplier.transport.configure", "Configure")}
                </FoodsButton>
            :   <MuiButton size="small" variant="text" disabled={disabled || hasCollectionError} onClick={onConfigure}>
                    {hasTransport ?
                        t("ingredientSupplier.transport.edit", "Edit")
                    :   t("ingredientSupplier.transport.configure", "Configure")}
                </MuiButton>
            }
        </Box>
    );
};

/** @deprecated Use IngredientSupplierTransportControl outside table-specific layouts. */
export const IngredientSupplierTransportTableCell = IngredientSupplierTransportControl;
